import type { EmailCreditErrorModalProps } from '../../types/modals'
/** Credit report error email popup — POST /api/email_credit_error (Angular EmailCreditErrorComponent). */
import { useEffect, useState } from 'react'
import { OnScreenModal } from '@/components/ui/OnScreenModal'
import { sendCreditErrorEmail } from '../../services/applicationService'
import { showSuccess, showError } from '@/lib/utils/notifications'
import modalStyles from '../../styles/modals.module.css'


const tid = 'application-modal-email-credit-error'

export function EmailCreditErrorModal({
  isOpen,
  applicantId,
  onClose,
  onSuccess,
}: EmailCreditErrorModalProps) {
  const [securityFreeze, setSecurityFreeze] = useState(false)
  const [creditError, setCreditError] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setSecurityFreeze(false)
      setCreditError(false)
    }
  }, [isOpen])

  const canSubmit = (securityFreeze || creditError) && !submitting

  const handleSubmit = async () => {
    if (!canSubmit) return
    setSubmitting(true)
    try {
      await sendCreditErrorEmail({
        applicant_id: applicantId,
        security_freeze: securityFreeze,
        credit_error: creditError,
      })
      showSuccess('Mail Sent Successfully!')
      onSuccess?.()
      onClose()
    } catch (error: unknown) {
      const msg = (error as { message?: unknown })?.message
      showError(typeof msg === 'string' && msg ? msg : 'Failed to send email.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <OnScreenModal
      isOpen={isOpen}
      title="Send Credit Report Error Email"
      onClose={onClose}
      maxWidth="max-w-xl"
      overlayDataTestId={tid}
      panelDataTestId={`${tid}-panel`}
      titleDataTestId={`${tid}-title`}
      closeButtonDataTestId={`${tid}-close-btn`}
      bodyScrollOnly
      footer={
        <div className={modalStyles.modalFooterActionGroup} data-testid={`${tid}-footer`}>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            data-testid={`${tid}-submit-button`}
            className={modalStyles.modalActionPrimary}
          >
            {submitting ? 'Sending...' : 'Submit'}
          </button>
          <button
            type="button"
            onClick={onClose}
            data-testid={`${tid}-cancel-btn`}
            className={modalStyles.modalActionSecondary}
          >
            Cancel
          </button>
        </div>
      }
    >
      <div className="space-y-4" data-testid={`${tid}-body`}>
        <label className="flex items-start gap-3 text-sm text-slate-900 cursor-pointer" data-testid={`${tid}-security-freeze-label`}>
          <input
            type="checkbox"
            className="mt-1 h-4 w-4 cursor-pointer shrink-0"
            checked={securityFreeze}
            onChange={(e) => setSecurityFreeze(e.target.checked)}
            data-testid={`${tid}-security-freeze-input`}
          />
          <span>
            <b>Issue: Security Freeze on your credit :</b> While trying to
            process your application, we received a response that you have a
            "Security Freeze" on your credit reports, which is preventing us
            from processing your application at this time. We have temporarily
            'suspended' your application, until we get an update from you.
          </span>
        </label>

        <label className="flex items-start gap-3 text-sm text-slate-900 cursor-pointer" data-testid={`${tid}-credit-error-label`}>
          <input
            type="checkbox"
            className="mt-1 h-4 w-4 cursor-pointer shrink-0"
            checked={creditError}
            onChange={(e) => setCreditError(e.target.checked)}
            data-testid={`${tid}-credit-error-input`}
          />
          <span>
            <b>Issue: Error while pulling your credit report :</b> We have
            tried to pull your credit report but receive errors based on the
            information you have provided, which is preventing us from
            processing your application at this time. We have temporarily
            'suspended' your application, until we get an update from you.
          </span>
        </label>

      </div>
    </OnScreenModal>
  )
}
